import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Search } from 'lucide-react';
import CRMLayout from '../../components/CRM/CRMLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomeLoanReferrals() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchReferrals = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/admin/home-loan-referrals`, {
        params: { page, page_size: 25, search, sort_by: sortBy, sort_order: sortOrder },
      });
      setData(response.data);
    } catch (fetchError) {
      setError(fetchError?.response?.data?.detail || 'Unable to load referrals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [page, sortBy, sortOrder]);

  const onSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    fetchReferrals();
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/home-loan-referrals/export`, {
        params: { search, sort_by: sortBy, sort_order: sortOrder },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'home-loan-referrals.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <CRMLayout>
      <div className="min-h-screen bg-navy-900 text-white pb-20" data-testid="home-loan-referrals-page">
        <header className="border-b border-white/10 bg-navy-950/70 px-6 py-6" data-testid="home-loan-referrals-header">
          <h1 className="text-3xl font-heading uppercase tracking-wide" data-testid="home-loan-referrals-heading">
            Home Loan Referrals
          </h1>
          <p className="text-sm text-white/70 mt-2" data-testid="home-loan-referrals-subheading">
            Internal referral records for Flash Funding × Corelend enquiries.
          </p>
        </header>

        <section className="px-6 py-6 space-y-6" data-testid="home-loan-referrals-main-section">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="home-loan-referrals-stats-grid">
            <article className="rounded-xl border border-purple-500/30 bg-navy-800/70 p-5" data-testid="home-loan-referrals-stat-month">
              <p className="text-xs uppercase tracking-[0.14em] text-purple-200">This Month</p>
              <p className="text-3xl font-bold mt-2" data-testid="home-loan-referrals-stat-month-value">{data?.stats?.this_month ?? 0}</p>
            </article>
            <article className="rounded-xl border border-blue-500/30 bg-navy-800/70 p-5" data-testid="home-loan-referrals-stat-year">
              <p className="text-xs uppercase tracking-[0.14em] text-blue-200">This Year</p>
              <p className="text-3xl font-bold mt-2" data-testid="home-loan-referrals-stat-year-value">{data?.stats?.this_year ?? 0}</p>
            </article>
            <article className="rounded-xl border border-emerald-500/30 bg-navy-800/70 p-5" data-testid="home-loan-referrals-stat-all-time">
              <p className="text-xs uppercase tracking-[0.14em] text-emerald-200">All Time</p>
              <p className="text-3xl font-bold mt-2" data-testid="home-loan-referrals-stat-all-time-value">{data?.stats?.all_time ?? 0}</p>
            </article>
            <article className="rounded-xl border border-amber-500/30 bg-navy-800/70 p-5" data-testid="home-loan-referrals-stat-spam">
              <p className="text-xs uppercase tracking-[0.14em] text-amber-200">Flagged Spam</p>
              <p className="text-3xl font-bold mt-2" data-testid="home-loan-referrals-stat-spam-value">{data?.stats?.flagged_spam ?? 0}</p>
            </article>
          </div>

          <form onSubmit={onSearchSubmit} className="flex flex-wrap items-end gap-3" data-testid="home-loan-referrals-controls-form">
            <div className="min-w-[260px]">
              <label className="text-xs uppercase tracking-[0.14em] text-white/65" data-testid="home-loan-referrals-search-label">Search</label>
              <div className="relative mt-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9 bg-navy-800 border-purple-500/25 text-white"
                  placeholder="Name, email, phone, suburb"
                  data-testid="home-loan-referrals-search-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-white/65" data-testid="home-loan-referrals-sort-by-label">Sort by</label>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="h-10 mt-2 rounded-lg border border-purple-500/25 bg-navy-800 px-3 text-sm"
                data-testid="home-loan-referrals-sort-by-select"
              >
                <option value="created_at">Submitted</option>
                <option value="full_name">Full Name</option>
                <option value="loan_purpose">Loan Purpose</option>
                <option value="suburb_state">Suburb / State</option>
              </select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-white/65" data-testid="home-loan-referrals-sort-order-label">Order</label>
              <select
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                className="h-10 mt-2 rounded-lg border border-purple-500/25 bg-navy-800 px-3 text-sm"
                data-testid="home-loan-referrals-sort-order-select"
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
            </div>

            <Button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="home-loan-referrals-search-button">Apply</Button>
            <Button type="button" onClick={onExport} disabled={exporting} className="bg-navy-800 border border-purple-500/25 text-white hover:bg-purple-500/15" data-testid="home-loan-referrals-export-button">
              <Download size={14} className="mr-2" /> {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </form>

          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-4 text-red-200" data-testid="home-loan-referrals-error">
              {error}
            </div>
          )}

          <div className="rounded-xl border border-white/10 bg-navy-800/60 overflow-x-auto" data-testid="home-loan-referrals-table-wrap">
            <table className="w-full min-w-[1200px]" data-testid="home-loan-referrals-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-white/65">
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Full Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Suburb / State</th>
                  <th className="px-4 py-3">Purpose</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Consent</th>
                  <th className="px-4 py-3">CAPTCHA</th>
                  <th className="px-4 py-3">Email Sent</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-white/60" data-testid="home-loan-referrals-loading">Loading referrals...</td>
                  </tr>
                ) : data?.items?.length ? (
                  data.items.map((item) => (
                    <tr key={item.id} className="border-b border-white/5 text-sm" data-testid={`home-loan-referrals-row-${item.id}`}>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-created-${item.id}`}>{new Date(item.created_at).toLocaleString('en-AU')}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-name-${item.id}`}>{item.full_name}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-email-${item.id}`}>{item.email}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-phone-${item.id}`}>{item.phone}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-suburb-${item.id}`}>{item.suburb_state}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-purpose-${item.id}`}>{item.loan_purpose}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-amount-${item.id}`}>{item.approximate_loan_amount}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-consent-${item.id}`}>{item.consent ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-captcha-${item.id}`}>{item.captcha_status || 'N/A'}</td>
                      <td className="px-4 py-3" data-testid={`home-loan-referrals-email-status-${item.id}`}>{item.admin_email_sent ? 'Admin ✅' : 'Admin ❌'} / {item.lead_confirmation_sent ? 'Lead ✅' : 'Lead ❌'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-white/60" data-testid="home-loan-referrals-empty">No referrals found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-white/70" data-testid="home-loan-referrals-pagination-wrap">
            <p data-testid="home-loan-referrals-pagination-summary">
              Page {data?.pagination?.page || 1} of {Math.max(data?.pagination?.total_pages || 1, 1)}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={(data?.pagination?.page || 1) <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="border-purple-500/25 bg-navy-800 text-white"
                data-testid="home-loan-referrals-prev-page-button"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={(data?.pagination?.page || 1) >= (data?.pagination?.total_pages || 1)}
                onClick={() => setPage((prev) => prev + 1)}
                className="border-purple-500/25 bg-navy-800 text-white"
                data-testid="home-loan-referrals-next-page-button"
              >
                Next
              </Button>
            </div>
          </div>
        </section>
      </div>
    </CRMLayout>
  );
}
